import React, { useState, useRef, useCallback, useEffect } from 'react';

interface SliderProps {
  orientation?: 'horizontal' | 'vertical';
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  showSteps?: boolean;
  showValue?: boolean;
  trackLength?: number;
  trackThickness?: number;
  stepLabels?: string[] | ((value: number) => string);
  disabled?: boolean;
}

/**
 * Slider with fixed right/top fill
 * 
 * - Horizontal: Fill extends from RIGHT edge toward handle
 * - Vertical: Fill extends from TOP edge toward handle
 * - Snaps to step values
 */
export function Slider({
  orientation = 'horizontal',
  min = 1,
  max = 10,
  step = 1,
  value: controlledValue,
  defaultValue,
  onChange,
  showSteps = true,
  showValue = true,
  trackLength = 600,
  trackThickness = 4,
  stepLabels = null,
  disabled = false,
}: SliderProps) {
  const isHorizontal = orientation === 'horizontal';
  
  const [internalValue, setInternalValue] = useState(defaultValue ?? min);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;
  
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const totalSteps = Math.floor((max - min) / step);
  const stepMarkers = Array.from({ length: totalSteps + 1 }, (_, i) => min + i * step);

  const snapToStep = useCallback((val: number) => {
    const snapped = Math.round((val - min) / step) * step + min;
    // Never reach the max - stop one step before
    const maxAllowed = max - step;
    return Math.max(min, Math.min(maxAllowed, snapped));
  }, [min, max, step]);

  const positionToValue = useCallback((clientX: number, clientY: number) => {
    if (!trackRef.current) return currentValue;
    
    const rect = trackRef.current.getBoundingClientRect();
    let ratio: number;
    
    if (isHorizontal) {
      const x = clientX - rect.left;
      ratio = x / rect.width;
    } else {
      const y = clientY - rect.top;
      ratio = 1 - (y / rect.height);
    }
    
    ratio = Math.max(0, Math.min(1, ratio));
    const rawValue = min + ratio * (max - min);
    return snapToStep(rawValue);
  }, [isHorizontal, min, max, currentValue, snapToStep]);

  const updateValue = useCallback((newValue: number) => {
    if (disabled) return;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  }, [disabled, isControlled, onChange]);

  const handleStart = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    isDragging.current = true;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const newValue = positionToValue(clientX, clientY);
    updateValue(newValue);
  }, [disabled, positionToValue, updateValue]);

  const handleMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging.current || disabled) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const newValue = positionToValue(clientX, clientY);
    updateValue(newValue);
  }, [disabled, positionToValue, updateValue]);

  const handleEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [handleMove, handleEnd]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    
    let newValue = currentValue;
    const maxAllowed = max - step;
    
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = Math.min(maxAllowed, currentValue + step);
        e.preventDefault();
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = Math.max(min, currentValue - step);
        e.preventDefault();
        break;
      case 'Home':
        newValue = min;
        e.preventDefault();
        break;
      case 'End':
        newValue = maxAllowed;
        e.preventDefault();
        break;
      default:
        return;
    }
    
    updateValue(newValue);
  }, [disabled, currentValue, min, max, step, updateValue]);

  // Handle position as percentage
  const handlePercent = ((currentValue - min) / (max - min)) * 100;
  // Fill goes from handle to right/top edge
  const fillPercent = 100 - handlePercent;
  
  const handleSize = 18;

  // Get label for a step value
  const getStepLabel = (stepValue: number): string | null => {
    if (!stepLabels) return null;
    if (typeof stepLabels === 'function') {
      return stepLabels(stepValue);
    }
    if (Array.isArray(stepLabels)) {
      const index = Math.round((stepValue - min) / step);
      return stepLabels[index] || null;
    }
    return null;
  };

  const currentLabel = getStepLabel(currentValue);
  const maxLabel = getStepLabel(max);

  return (
    <div className={`inline-flex ${isHorizontal ? 'flex-col' : 'flex-row'} items-center gap-4 ${disabled ? 'opacity-50' : ''}`}>
      {showValue && (
        <div className="text-sm font-medium text-muted-foreground min-w-[28px] text-center">
          {currentValue}{currentLabel ? ` — ${currentLabel}` : ''}
        </div>
      )}
      
      <div
        ref={trackRef}
        className={`relative ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${isHorizontal && maxLabel ? 'mb-7' : ''} ${!isHorizontal && maxLabel ? 'mr-[60px]' : ''}`}
        style={{
          width: isHorizontal ? trackLength : handleSize,
          height: isHorizontal ? handleSize : trackLength,
        }}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={currentValue}
        aria-orientation={orientation}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
      >
        {/* Track background */}
        <div
          className="absolute bg-slate-200 rounded-full"
          style={{
            ...(isHorizontal
              ? { left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: trackThickness }
              : { top: 0, bottom: 0, left: '50%', transform: 'translateX(-50%)', width: trackThickness }),
            borderRadius: trackThickness,
          }}
        />
        
        {/* Fill - from RIGHT (horizontal) or TOP (vertical) */}
        <div
          className="absolute bg-blue-500 rounded-full"
          style={{
            borderRadius: trackThickness,
            ...(isHorizontal
              ? {
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: trackThickness,
                  width: `${fillPercent}%`,
                }
              : {
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: trackThickness,
                  height: `${fillPercent}%`,
                }),
          }}
        />
        
        {/* Step markers */}
        {showSteps && stepMarkers.map((stepValue) => {
          const percent = ((stepValue - min) / (max - min)) * 100;
          const isInFill = percent >= handlePercent;
          return (
            <div
              key={stepValue}
              className={`absolute w-1.5 h-1.5 rounded-full z-10 ${isInFill ? 'bg-blue-500' : 'bg-slate-300'}`}
              style={{
                ...(isHorizontal
                  ? { left: `${percent}%`, top: '50%', transform: 'translate(-50%, -50%)' }
                  : { bottom: `${percent}%`, left: '50%', transform: 'translate(-50%, 50%)' }),
              }}
            />
          );
        })}
        
        {/* Max label underneath the max point */}
        {maxLabel && (
          <div
            className="absolute text-xs font-medium text-blue-500 whitespace-nowrap text-center"
            style={{
              ...(isHorizontal
                ? { right: 0, top: '100%', marginTop: 8, transform: 'translateX(50%)' }
                : { top: 0, left: '100%', marginLeft: 8, transform: 'translateY(-50%)' }),
            }}
          >
            {maxLabel}
          </div>
        )}
        
        {/* Handle */}
        <div
          className={`absolute bg-white border-2 border-blue-500 rounded-full shadow-sm z-20 ${disabled ? 'cursor-not-allowed' : 'cursor-grab'}`}
          style={{
            width: handleSize,
            height: handleSize,
            ...(isHorizontal
              ? { left: `${handlePercent}%`, top: '50%', transform: 'translate(-50%, -50%)' }
              : { bottom: `${handlePercent}%`, left: '50%', transform: 'translate(-50%, 50%)' }),
          }}
        />
      </div>
    </div>
  );
}
